/**
 * Medication Administration Record (MAR) Form
 * Based on NDIS Practice Standards Core Module 4.3
 * All required fields for compliant medication administration
 */

import React, { useState } from 'react';
import { 
  Pill, CheckCircle, XCircle, Clock, User, FileText,
  Save, AlertTriangle, Package, X
} from 'lucide-react';
import BarcodeScanner from './BarcodeScanner';
import { useToast } from '../Common/ToastProvider';

const MARForm = ({ participantId, medication, onSave, onCancel }) => {
  const { showToast } = useToast();
  const [showBarcodeScanner, setShowBarcodeScanner] = useState(false);
  const [barcodeData, setBarcodeData] = useState(null);
  const [status, setStatus] = useState('administered'); // administered, missed, refused
  const [missedRefusedReason, setMissedRefusedReason] = useState('');
  const [missedRefusedNotes, setMissedRefusedNotes] = useState('');
  
  const [formData, setFormData] = useState({
    participantName: medication?.participantName || '',
    participantDOB: medication?.participantDOB || '',
    medicationName: medication?.name || '',
    activeIngredient: medication?.activeIngredient || '',
    dosage: medication?.dosage || '',
    strength: medication?.strength || '',
    route: medication?.route || 'oral',
    frequency: medication?.frequency || '',
    prescriberName: medication?.prescriber || '',
    prescriberContact: medication?.prescriberContact || '',
    prescriptionDate: medication?.prescriptionDate || '',
    scheduledTime: medication?.scheduledTime || new Date().toISOString().slice(0, 16),
    actualAdministrationTime: new Date().toISOString().slice(0, 16),
    administeringWorkerName: '',
    witnessRequired: medication?.isSchedule8 || false,
    witnessName: '',
    outcomeObservations: '',
    sideEffects: '',
    response: 'positive',
    barcode: null,
    batch: null,
    expiry: null
  });

  const handleBarcodeScan = (scanData) => {
    setBarcodeData(scanData);
    // Auto-populate form fields from barcode
    setFormData({
      ...formData,
      medicationName: scanData.medication || formData.medicationName,
      batch: scanData.batch || formData.batch,
      expiry: scanData.expiry || formData.expiry,
      barcode: scanData.barcode || formData.barcode
    });
    setShowBarcodeScanner(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate based on status
    if (status === 'administered') {
      // Validate required fields for administration
      if (!formData.medicationName || !formData.activeIngredient) {
        showToast('Medication name and active ingredient are required', 'error');
        return;
      }
      
      if (!formData.administeringWorkerName) {
        showToast('Administering worker name is required', 'error');
        return;
      }
      
      if (formData.witnessRequired && !formData.witnessName) {
        showToast('Witness name is required for Schedule 8 medications', 'error');
        return;
      }
      
      if (!formData.outcomeObservations) {
        showToast('Outcome observations are required', 'error');
        return;
      }
    } else if (status === 'missed' || status === 'refused') {
      if (!missedRefusedReason.trim()) {
        showToast(`Please provide a reason for ${status === 'missed' ? 'missed' : 'refused'} dose`, 'error');
        return;
      }
    }
    
    // Mock marRecord - createMedicationAdministration was removed
    const marRecord = {
      participantId,
      medicationId: medication?.id,
      ...formData,
      status: status,
      missedReason: status === 'missed' ? missedRefusedReason : null,
      missedNotes: status === 'missed' ? missedRefusedNotes : null,
      refusedReason: status === 'refused' ? missedRefusedReason : null,
      refusedNotes: status === 'refused' ? missedRefusedNotes : null,
      missedAt: status === 'missed' ? new Date().toISOString() : null,
      refusedAt: status === 'refused' ? new Date().toISOString() : null
    };

    if (onSave) {
      await onSave(marRecord);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Status Selection */}
        <div className="bg-gray-50 p-4 rounded-lg border-2 border-gray-200">
          <h3 className="font-semibold text-gray-900 mb-3">Administration Status</h3>
          <div className="grid grid-cols-3 gap-3">
            <button
              type="button"
              onClick={() => setStatus('administered')}
              className={`px-4 py-3 rounded-lg font-semibold transition-colors ${
                status === 'administered'
                  ? 'bg-green-600 text-white'
                  : 'bg-white text-gray-700 border-2 border-gray-300 hover:bg-green-50'
              }`}
            >
              <CheckCircle className="w-5 h-5 inline mr-2" />
              Administered
            </button>
            <button
              type="button"
              onClick={() => setStatus('missed')}
              className={`px-4 py-3 rounded-lg font-semibold transition-colors ${
                status === 'missed'
                  ? 'bg-orange-600 text-white'
                  : 'bg-white text-gray-700 border-2 border-gray-300 hover:bg-orange-50'
              }`}
            >
              <XCircle className="w-5 h-5 inline mr-2" />
              Missed
            </button>
            <button
              type="button"
              onClick={() => setStatus('refused')}
              className={`px-4 py-3 rounded-lg font-semibold transition-colors ${
                status === 'refused'
                  ? 'bg-red-600 text-white'
                  : 'bg-white text-gray-700 border-2 border-gray-300 hover:bg-red-50'
              }`}
            >
              <X className="w-5 h-5 inline mr-2" />
              Refused
            </button>
          </div>
        </div>

        {/* Missed/Refused Reason */}
        {(status === 'missed' || status === 'refused') && (
          <div className="bg-red-50 p-4 rounded-lg border-2 border-red-200">
            <h3 className="font-semibold text-red-900 mb-3">
              {status === 'missed' ? 'Missed' : 'Refused'} Dose Documentation
            </h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Reason <span className="text-red-600">*</span>
                </label>
                <select
                  value={missedRefusedReason}
                  onChange={(e) => setMissedRefusedReason(e.target.value)}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="">Select reason...</option>
                  {status === 'missed' ? (
                    <>
                      <option value="participant_unavailable">Participant Unavailable</option>
                      <option value="medication_unavailable">Medication Unavailable</option>
                      <option value="staff_error">Staff Error</option>
                      <option value="system_error">System Error</option>
                      <option value="other">Other</option>
                    </>
                  ) : (
                    <>
                      <option value="participant_refused">Participant Refused</option>
                      <option value="participant_unable">Participant Unable to Take</option>
                      <option value="safety_concern">Safety Concern</option>
                      <option value="allergy_concern">Allergy Concern</option>
                      <option value="other">Other</option>
                    </>
                  )}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Additional Notes
                </label>
                <textarea
                  value={missedRefusedNotes}
                  onChange={(e) => setMissedRefusedNotes(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="Provide additional details..."
                />
              </div>
            </div>
          </div>
        )}

        {/* Participant Identification */}
      <div className="bg-blue-50 p-4 rounded-lg">
        <h3 className="font-semibold text-blue-900 mb-2">Participant Identification (Required)</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Full Name <span className="text-red-600">*</span>
            </label>
            <input
              type="text"
              value={formData.participantName}
              onChange={(e) => setFormData({...formData, participantName: e.target.value})}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Date of Birth <span className="text-red-600">*</span>
            </label>
            <input
              type="date"
              value={formData.participantDOB}
              onChange={(e) => setFormData({...formData, participantDOB: e.target.value})}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
        </div>
      </div>

      {/* Medication Details */}
      <div className="bg-green-50 p-4 rounded-lg">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold text-green-900">Medication Details (Required)</h3>
          {status === 'administered' && (
            <button
              type="button"
              onClick={() => setShowBarcodeScanner(true)}
              className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2 text-sm"
            >
              <Package className="w-4 h-4" />
              Scan Barcode
            </button>
          )}
        </div>
        {barcodeData && (
          <div className="mb-3 p-3 bg-green-100 border border-green-300 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="text-sm font-medium text-green-800">
                  Barcode scanned: {barcodeData.barcode}
                </span>
              </div>
              <button
                type="button"
                onClick={() => {
                  setBarcodeData(null);
                  setFormData({...formData, barcode: null, batch: null, expiry: null});
                }}
                className="text-green-600 hover:text-green-800"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            {barcodeData.batch && (
              <p className="text-xs text-green-700 mt-1">Batch: {barcodeData.batch} | Expiry: {barcodeData.expiry}</p>
            )}
          </div>
        )}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Medication Name (Brand) <span className="text-red-600">*</span>
            </label>
            <input
              type="text"
              value={formData.medicationName}
              onChange={(e) => setFormData({...formData, medicationName: e.target.value})}
              required={status === 'administered'}
              disabled={!!barcodeData}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg disabled:bg-gray-100"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Active Ingredient <span className="text-red-600">*</span>
            </label>
            <input
              type="text"
              value={formData.activeIngredient}
              onChange={(e) => setFormData({...formData, activeIngredient: e.target.value})}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Dosage/Strength <span className="text-red-600">*</span>
            </label>
            <input
              type="text"
              value={formData.dosage}
              onChange={(e) => setFormData({...formData, dosage: e.target.value})}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Route <span className="text-red-600">*</span>
            </label>
            <select
              value={formData.route}
              onChange={(e) => setFormData({...formData, route: e.target.value})}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="oral">Oral</option>
              <option value="topical">Topical</option>
              <option value="subcutaneous">Subcutaneous</option>
              <option value="intramuscular">Intramuscular</option>
              <option value="intravenous">Intravenous</option>
              <option value="inhalation">Inhalation</option>
            </select>
          </div>
        </div>
      </div>

      {/* Administration Details - Only show if administered */}
      {status === 'administered' && (
      <div className="bg-purple-50 p-4 rounded-lg">
        <h3 className="font-semibold text-purple-900 mb-2">Administration Details (Required)</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Administering Worker Name <span className="text-red-600">*</span>
            </label>
            <input
              type="text"
              value={formData.administeringWorkerName}
              onChange={(e) => setFormData({...formData, administeringWorkerName: e.target.value})}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Actual Administration Time <span className="text-red-600">*</span>
            </label>
            <input
              type="datetime-local"
              value={formData.actualAdministrationTime}
              onChange={(e) => setFormData({...formData, actualAdministrationTime: e.target.value})}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
        </div>

        {formData.witnessRequired && (
          <div className="mt-4">
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Witness Name (Required for S8) <span className="text-red-600">*</span>
            </label>
            <input
              type="text"
              value={formData.witnessName}
              onChange={(e) => setFormData({...formData, witnessName: e.target.value})}
              required={formData.witnessRequired}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
        )}
      </div>
      )}

      {/* Outcome Observations - Only show if administered */}
      {status === 'administered' && (
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Outcome Observations <span className="text-red-600">*</span>
        </label>
        <textarea
          value={formData.outcomeObservations}
          onChange={(e) => setFormData({...formData, outcomeObservations: e.target.value})}
          required={status === 'administered'}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
          placeholder="Document response, side effects, and observations..."
        />
      </div>
      )}

      <div className="flex gap-4 pt-4">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center gap-2"
        >
          <Save className="w-4 h-4" />
          Record Administration
        </button>
      </div>
    </form>

    {/* Barcode Scanner Modal */}
    {showBarcodeScanner && (
      <BarcodeScanner
        isOpen={showBarcodeScanner}
        onScan={handleBarcodeScan}
        onClose={() => setShowBarcodeScanner(false)}
      />
    )}
    </>
  );
};

export default MARForm;
